<template>
  <TemplateHead :config="config" />

  <div
    :class="fonts[config.style.font]"
    class="min-h-screen"
  >
    <div class="w-full flex justify-center bg-gray-100">
      <div class="w-full lg:w-[80rem]">
        <Header
          :config="config"
          :primary-color="primaryColor"
          :secondary-color="secondaryColor"
        />

        <div
          :style="headerBackground"
          class="h-[55rem] sm:h-[45rem] w-full"
        >
          <div class="w-full grid grid-cols-1 lg:grid-cols-2">
            <div class="col-start-2 lg:px-16">
              <LandingForm
                :can-submit="true"
                :config="config"
                :quiz-config="quizConfig"
                @submit="submitForm"
              />
            </div>
          </div>
        </div>

        <Blocks :config="config" />

        <Reviews :config="config" />

        <FooterImage
          :background-style="footerBackground"
          :config="config"
        />
      </div>
    </div>

    <Footer
      :config="config"
      :pages="pages"
      :collector-flow-uuid="collectorFlowUuid"
      :is-mobile="isMobile"
    />

    <ExitModal
      :show="showExitModal"
      :campaign-subs="exitCampaignSubs"
      :primary-color="primaryColor"
      :secondary-color="secondaryColor"
      @close="showExitModal = false"
    />
  </div>
</template>

<script setup>
import {defineProps, onMounted, onUnmounted, ref} from 'vue';
import axios from 'axios';
import {checkIfTargetingCookiesAreAccepted, fingerprint, isPreview, registerVisit} from '@timon/js/Mixins/helpers';
import {useStore} from 'vuex';
import Footer from '@timon/js/Templates/LANDER-001/Components/Footer.vue';
import ExitModal from '@timon/js/Components/ExitModal.vue';
import TemplateHead from '@timon/js/Components/TemplateHead.vue';
import FooterImage from '@timon/js/Templates/LANDER-001/Components/FooterImage.vue';
import Reviews from '@timon/js/Templates/LANDER-001/Components/Reviews.vue';
import Blocks from '@timon/js/Templates/LANDER-001/Components/Blocks.vue';
import LandingForm from '@timon/js/Templates/LANDER-001/Components/LandingForm.vue';
import Header from '@timon/js/Templates/LANDER-001/Components/Header.vue';

const props = defineProps({
  config: {
    type: Object,
    required: true,
  },

  quizConfig: {
    type: Object,
    required: true,
  },

  variables: {
    type: Object,
    required: true,
  },

  pages: {
    type: Array,
    required: true,
  },

  collectorFlowUuid: {
    type: String,
    required: true,
  },

  adUrlUuid: {
    type: String,
    required: false,
    default: null,
  },

  fingerprintRequired: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const fonts = {
  'mono': 'font-mono',
  'sans': 'font-sans',
  'serif': 'font-serif',
};

const form = {
  gender: '',
  birth_date: null,
  email: null,
  selected_co_reg_campaign_subs: [],
  first_name: null,
  last_name: null,
  postcode: null,
  terms_accepted: false,
};

const primaryColor = ref(props.config.style?.primary);
const secondaryColor = ref(props.config.style?.secondary);

const isMobile = ref(false);

const footerBackground = ref(null);
const headerBackground = ref(null);

const exitCampaignSubs = ref([]);

const landingVisitUuid = ref(null);

const fingerprintId = ref(null);
const canSubmit = ref(true);

const showExitModal = ref(false);

const store = useStore();

store.commit('errors/setErrors', []);
store.commit('form/setForm', form);

function updateIsMobile() {
  isMobile.value = window.innerWidth < 768;
}
function acceptedCookies() {
  checkIfTargetingCookiesAreAccepted(() => registerVisit(props.adUrlUuid));
}

onMounted(() => {
  updateIsMobile();

  window.addEventListener('resize', updateIsMobile);
  window.addEventListener('CookieScriptAcceptAll', acceptedCookies);
  window.addEventListener('CookieScriptAccept', acceptedCookies);

  const footerBackgroundUrl = props.config.footer?.background_url ?? new URL('@timon/js/Templates/LANDER-001/assets/footer-background.jpg', import.meta.url).href;
  footerBackground.value = {
    background: `#000 url(${footerBackgroundUrl}) center center no-repeat`,
    backgroundSize: 'cover',
    opacity: 0.8,
    backgroundAttachment: 'fixed',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
    borderStyle: 'none',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: '0px',
    borderRadius: '0px',
    position: 'relative',
  };

  const headerBackgroundUrl = props.config.header?.background_url ?? new URL('@timon/js/Templates/LANDER-001/assets/header-background.png', import.meta.url).href;
  headerBackground.value = {
    background: `#000 url(${headerBackgroundUrl}) center center no-repeat`,
    backgroundSize: 'cover',
  };

  if (!isPreview()) {
    registerVisit(props.adUrlUuid, (data) => {
      landingVisitUuid.value = data.visit_key;
    });
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', updateIsMobile);
  window.removeEventListener('CookieScriptAcceptAll', acceptedCookies);
  window.removeEventListener('CookieScriptAccept', acceptedCookies);
});

async function submitForm() {
  if (!canSubmit.value) {
    return;
  }

  store.commit('errors/clearErrors');
  canSubmit.value = false;

  if (isPreview()) {
    postForm();

    return;
  }

  if (props.fingerprintRequired) {
    try {
      const fp = await fingerprint();
      const result = await fp.get();
      fingerprintId.value = result.visitorId;

      postForm();

      return;
    } catch (error) {
      console.error(error);
    }
  }

  postForm();
}

function postForm() {
  axios.post(route('zazu.v1.landing.form.submit', {
    adUrl: props.adUrlUuid,
  }), {
    // only one step
    is_final_step: true,
    include_offer_wall: false,
    include_exit_wall: true,
    landing_visit_uuid: landingVisitUuid.value,
    fingerprint: fingerprintId.value,

    ...store.state.form,
  })
      .then((response) => {
        exitCampaignSubs.value = response.data.exit_wall;
        showExitModal.value = true;

        store.commit('form/clearForm');

        canSubmit.value = true;
      })
      .catch((error) => {
        if (error.response.data.errors) {
          store.commit('errors/setErrors', error.response.data.errors);
        }

        canSubmit.value = true;
      });
}
</script>
